"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Hero = void 0;
var react_1 = __importDefault(require("react"));
var Typography_1 = __importDefault(require("@mui/material/Typography"));
var ChannelValue_1 = require("../ChannelValue");
var css_1 = require("@emotion/css");
var prop_types_1 = __importDefault(require("prop-types"));
var Box_1 = __importDefault(require("@mui/material/Box"));
var styles_1 = require("@mui/material/styles");
var base_1 = require("@mui/base");
var HeroClasses_1 = require("./HeroClasses");
var useUtilityClasses = function (ownerState) {
    var classes = ownerState.classes;
    var slots = {
        root: ['root'],
        icon: ['icon'],
        label: ['label'],
        values: ['values'],
    };
    return (0, base_1.unstable_composeClasses)(slots, HeroClasses_1.getHeroUtilityClass, classes);
};
var normalizeIconSize = function (size) { return Math.max(10, size); };
var Root = (0, styles_1.styled)(Box_1.default, {})(function (_a) {
    var onClick = _a.onClick, theme = _a.theme;
    return ({
        fontSize: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flex: '1 1 0px',
        overflow: 'hidden',
        color: theme.palette.text.primary,
        padding: "1rem ".concat(theme.spacing()),
        cursor: onClick ? 'pointer' : 'inherit',
    });
});
var Icon = (0, styles_1.styled)(Box_1.default, {
    shouldForwardProp: function (prop) { return !['iconSize', 'iconBackgroundColor'].includes(prop.toString()); },
})(function (_a) {
    var iconSize = _a.iconSize, iconBackgroundColor = _a.iconBackgroundColor, theme = _a.theme;
    return ({
        lineHeight: 1,
        color: theme.palette.text.secondary,
        marginBottom: '.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: ".25rem ".concat(theme.spacing(0.5)),
        borderRadius: '50%',
        fontSize: typeof iconSize === 'number' ? normalizeIconSize(iconSize) : iconSize,
        height: typeof iconSize === 'number' ? Math.max(44, iconSize) : iconSize,
        width: typeof iconSize === 'number' ? Math.max(44, iconSize) : iconSize,
        backgroundColor: iconBackgroundColor,
    });
});
var Values = (0, styles_1.styled)(Box_1.default, {})(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.primary,
        lineHeight: 1.2,
        maxWidth: '100%',
        overflow: 'hidden',
        fontSize: '1.25rem',
    });
});
var Label = (0, styles_1.styled)(Typography_1.default, {})(function () { return ({
    fontSize: 'inherit',
    lineHeight: 1.2,
    letterSpacing: 0,
    fontWeight: 600,
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}); });
var HeroRender = function (props, ref) {
    var defaultClasses = useUtilityClasses(props);
    var classes = props.classes, userClassName = props.className, icon = props.icon, label = props.label, ChannelValueProps = props.ChannelValueProps, 
    // ignore unused vars so that we can do prop transferring to the root element
    /* eslint-disable @typescript-eslint/no-unused-vars */
    iconBackgroundColor = props.iconBackgroundColor, iconSize = props.iconSize, 
    /* eslint-enable @typescript-eslint/no-unused-vars */
    otherProps = __rest(props, ["classes", "className", "icon", "label", "ChannelValueProps", "iconBackgroundColor", "iconSize"]);
    return (react_1.default.createElement(Root, __assign({ ref: ref, className: (0, css_1.cx)(defaultClasses.root, classes.root, userClassName), "data-testid": 'blui-hero-root' }, otherProps),
        react_1.default.createElement(Icon, { className: (0, css_1.cx)(defaultClasses.icon, classes.icon), iconSize: iconSize, iconBackgroundColor: iconBackgroundColor }, icon),
        react_1.default.createElement(Values, { component: 'span', className: (0, css_1.cx)(defaultClasses.values, classes.values) },
            !props.children && (ChannelValueProps === null || ChannelValueProps === void 0 ? void 0 : ChannelValueProps.value) && (react_1.default.createElement(ChannelValue_1.ChannelValue, __assign({ fontSize: ChannelValueProps === null || ChannelValueProps === void 0 ? void 0 : ChannelValueProps.fontSize }, ChannelValueProps))),
            props.children),
        react_1.default.createElement(Label, { variant: 'body1', color: 'inherit', className: (0, css_1.cx)(defaultClasses.label, classes.label) }, label)));
};
/**
 * [Hero](https://brightlayer-ui-components.github.io/react/components/hero) component
 *
 * The `<Hero>` component displays a particular icon, value/units, and a label. The icon property will accept any valid component - this will typically be a Material icon, [Brightlayer UI icon](https://github.com/etn-ccis/blui-icons), or [Progress Icon](https://github.com/etn-ccis/blui-progress-icons). It will also accept Text/Emoji values.
 */
exports.Hero = react_1.default.forwardRef(HeroRender);
exports.Hero.displayName = 'Hero';
exports.Hero.propTypes = {
    classes: prop_types_1.default.shape({
        root: prop_types_1.default.string,
        values: prop_types_1.default.string,
        icon: prop_types_1.default.string,
        labels: prop_types_1.default.string,
    }),
    icon: prop_types_1.default.node.isRequired,
    iconBackgroundColor: prop_types_1.default.string,
    iconSize: prop_types_1.default.oneOfType([prop_types_1.default.number, prop_types_1.default.string]),
    label: prop_types_1.default.string.isRequired,
};
exports.Hero.defaultProps = {
    classes: {},
    iconBackgroundColor: 'transparent',
    iconSize: 36,
};
